import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { recipes } from '../services/api';
import { useAuth } from '../context/AuthContext';
import FavoriteButton from '../components/FavoriteButton';
import MadeButton from '../components/MadeButton';
import StarRating from '../components/StarRating';
import CommentList from '../components/CommentList';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import '../styles/RecipeDetail.css';
import '../styles/Print.css';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [userRating, setUserRating] = useState(null);
  const [averageRating, setAverageRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [hasMade, setHasMade] = useState(false);
  const [madeCount, setMadeCount] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const userData = JSON.parse(localStorage.getItem('user') || 'null');
      setCurrentUser(userData);
    }
  }, []);

  const fetchRecipe = async () => {
    try {
      const response = await recipes.getById(id);
      setRecipe(response.data);
      setLikesCount(response.data.likes?.length || 0);
      setIsLiked(response.data.likes?.includes(user?._id));
      setIsFavorited(response.data.isFavorited || false);
      setUserRating(response.data.userRating || null);
      setAverageRating(response.data.averageRating || 0);
      setRatingCount(response.data.ratingCount || 0);
      setHasMade(response.data.hasMade || false);
      setMadeCount(response.data.madeCount || 0);
    } catch (error) {
      console.error('Error fetching recipe:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      alert('Kérlek jelentkezz be a like-oláshoz!');
      return;
    }

    try {
      const response = await recipes.toggleLike(id);
      setIsLiked(response.data.isLiked);
      setLikesCount(response.data.likes);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Biztosan törölni szeretnéd ezt a receptet?')) {
      try {
        await recipes.delete(id);
        navigate('/recipes');
      } catch (error) {
        console.error('Error deleting recipe:', error);
        alert('Hiba történt a recept törlése során');
      }
    }
  };

  const handleApprove = async () => {
    if (window.confirm('Biztosan jóváhagyod ezt a receptet?')) {
      try {
        await recipes.approve(id);
        alert('Recept sikeresen jóváhagyva!');
        fetchRecipe(); // Frissítjük a receptet
      } catch (error) {
        console.error('Error approving recipe:', error);
        alert('Hiba történt a jóváhagyás során');
      }
    }
  };

  const handleReject = async () => {
    if (window.confirm('Biztosan elutasítod és törölni szeretnéd ezt a receptet?')) {
      try {
        await recipes.delete(id);
        alert('Recept elutasítva és törölve!');
        navigate('/admin');
      } catch (error) {
        console.error('Error rejecting recipe:', error);
        alert('Hiba történt az elutasítás során');
      }
    }
  };

  if (loading) {
    return <div className="container loading">Betöltés...</div>;
  }

  if (!recipe) {
    return <div className="container">Recept nem található</div>;
  }

  const canEdit = user && (user._id === recipe.author?._id || user.role === 'admin');
  const isAdmin = user && user.role === 'admin';

  return (
    <div className="recipe-detail">
      <div className="container">
        {/* Admin jóváhagyás panel - ha a recept nincs jóváhagyva */}
        {isAdmin && !recipe.isApproved && (
          <div className="admin-approval-panel">
            <div className="approval-info">
              <span className="approval-icon">⚠️</span>
              <span className="approval-text">Ez a recept jóváhagyásra vár</span>
            </div>
            <div className="approval-actions">
              <button onClick={handleApprove} className="btn btn-approve">
                ✅ Jóváhagyás
              </button>
              <button onClick={handleReject} className="btn btn-reject">
                ❌ Elutasítás
              </button>
            </div>
          </div>
        )}

        <div className="recipe-header">
          <div className="recipe-title-section">
            <h1>{recipe.title}</h1>
            <div className="recipe-badges">
              <span className="badge category-badge">{recipe.category}</span>
              <span className="badge difficulty-badge">{recipe.difficulty}</span>
              {!recipe.isApproved && (
                <span className="badge pending-badge">Jóváhagyásra vár</span>
              )}
            </div>
          </div>

          <div className="recipe-actions">
            <button onClick={handleLike} className={`like-btn ${isLiked ? 'liked' : ''}`}>
              ❤️ {likesCount}
            </button>
            <FavoriteButton
              recipeId={recipe._id}
              initialFavorited={isFavorited}
              onToggle={(favorited) => setIsFavorited(favorited)}
            />
            <button
              onClick={() => window.print()}
              className="btn btn-outline print-btn"
              title="Recept nyomtatása"
            >
              🖨️ Nyomtatás
            </button>
            {canEdit && (
              <>
                <Link to={`/edit-recipe/${id}`} className="btn btn-secondary">
                  Szerkesztés
                </Link>
                <button onClick={handleDelete} className="btn btn-outline">
                  Törlés
                </button>
              </>
            )}
          </div>
        </div>

        <div className="recipe-info">
          <div className="info-item">
            <strong>⏱️ Előkészítés:</strong> {recipe.prepTime} perc
          </div>
          <div className="info-item">
            <strong>🔥 Főzés:</strong> {recipe.cookTime} perc
          </div>
          <div className="info-item">
            <strong>👥 Adagok:</strong> {recipe.servings}
          </div>
          <div className="info-item">
            <strong>👨‍🍳 Készítette:</strong>{' '}
            <Link to={`/user/${recipe.author._id}`} className="author-link">
              {recipe.author.nickname || recipe.author.name}
            </Link>
          </div>
        </div>


        {/* Rating and Made Section */}
        <div className="recipe-rating-made-section">
          <div className="recipe-rating">
            <h3>Értékelés</h3>
            <StarRating
              recipeId={recipe._id}
              initialRating={userRating}
              averageRating={averageRating}
              ratingCount={ratingCount}
              onRate={(data) => {
                console.log('📊 RecipeDetail: onRate callback received:', data);
                console.log('📊 RecipeDetail: Setting states - averageRating:', data.averageRating, 'ratingCount:', data.ratingCount, 'userRating:', data.userRating);
                setAverageRating(data.averageRating);
                setRatingCount(data.ratingCount);
                setUserRating(data.userRating);
              }}
            />
          </div>
          <div className="recipe-made">
            <MadeButton
              recipeId={recipe._id}
              initialMade={hasMade}
              initialCount={madeCount}
              onToggle={(data) => {
                setHasMade(data.hasMade);
                setMadeCount(data.madeCount);
              }}
            />
          </div>
        </div>

        <div className="recipe-description">
          <p>{recipe.description}</p>
        </div>

        <div className="recipe-content">
          <div className="ingredients-section">
            <h2>Hozzávalók</h2>
            <ul className="ingredients-list">
              {recipe.ingredients.map((ingredient, index) => (
                <li key={index}>
                  <strong>{ingredient.amount}</strong> {ingredient.unit} {ingredient.name}
                </li>
              ))}
            </ul>
          </div>

          <div className="instructions-section">
            <h2>Elkészítés</h2>
            <ol className="instructions-list">
              {recipe.instructions
                .sort((a, b) => a.step - b.step)
                .map((instruction) => (
                  <li key={instruction.step}>
                    <p>{instruction.description}</p>
                  </li>
                ))}
            </ol>
          </div>
        </div>

        {recipe.tags && recipe.tags.length > 0 && (
          <div className="recipe-tags">
            <strong>Címkék:</strong>
            {recipe.tags.map((tag, index) => (
              <span key={index} className="tag">
                #{tag}
              </span>
            ))}
          </div>
        )}


        {/* Képgaléria - fedőkép + további képek */}
        {(() => {
          const allImages = [];

          // Fedőkép hozzáadása az elejére
          if (recipe.coverImage) {
            const coverUrl = typeof recipe.coverImage === 'object'
              ? recipe.coverImage.large
              : recipe.coverImage;
            allImages.push(coverUrl);
          }

          // További képek hozzáadása
          if (recipe.images && recipe.images.length > 0) {
            allImages.push(...recipe.images);
          }

          return allImages.length > 0 && (
            <div className="recipe-images-section">
              <h2>Képek</h2>
              <div className="images-carousel">
                <div className="main-image">
                  <img
                    src={selectedImage || allImages[0]}
                    alt={recipe.title}
                    onError={(e) => e.target.src = '/placeholder-recipe.png'}
                    onClick={() => {
                      const currentIndex = selectedImage ? allImages.indexOf(selectedImage) : 0;
                      setPhotoIndex(currentIndex);
                      setLightboxOpen(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </div>

                {allImages.length > 1 && (
                  <div className="image-thumbnails">
                    {allImages.map((img, idx) => (
                      <div
                        key={idx}
                        className={`thumbnail ${selectedImage === img ? 'active' : ''}`}
                        onClick={() => setSelectedImage(img)}
                      >
                        <img
                          src={img}
                          alt={`${recipe.title} ${idx + 1}`}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Comments Section */}
        <CommentList
          recipeId={recipe._id}
          currentUser={currentUser}
        />
      </div>

      {/* Lightbox for image zoom */}
      {lightboxOpen && (() => {
        const allImages = [];
        if (recipe.coverImage) {
          const coverUrl = typeof recipe.coverImage === 'object'
            ? recipe.coverImage.large
            : recipe.coverImage;
          allImages.push(coverUrl);
        }
        if (recipe.images && recipe.images.length > 0) {
          allImages.push(...recipe.images);
        }

        return (
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            index={photoIndex}
            slides={allImages.map(src => ({ src }))}
            on={{
              view: ({ index }) => setPhotoIndex(index)
            }}
          />
        );
      })()}
    </div>
  );
};

export default RecipeDetail;
